import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { InfoButton, InfoButtonTrigger } from '@optools/components/info-button';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@optools/components/ui/collapsible';
import { BTC_GAME_CONTENT_WITH_ASSETS } from '@optools/services/operator-gateway/btc/queries';
import { BtcGameAsset, BtcSchedulePipeline } from '@optools/services/operator-gateway/btc/types';
import { MediaType } from '@optools/services/operator-gateway/types/generated/graphql';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronUp, RefreshCcw } from 'lucide-react';

import { BulkAssetUpdatesDialog } from './bulk-asset-updates/bulk-asset-updates-dialog';
import { generateAssetKey } from '../utils/assets';
import { MediaDetails } from './media-details';
import { PipelineAssets } from './pipeline-assets';
import { PlaybackVerificationSheet } from './playback-verification-sheet';
import { UpdatePipelineMediaButton } from './update-pipeline-media-button';

export interface PipelineMediaProps {
  gamePk: number;
  pipeline: BtcSchedulePipeline;
}

export function PipelineMedia({ gamePk, pipeline }: PipelineMediaProps) {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule' });
  const { mediaId: mediaIdParam } = useSearch({
    from: '/_auth/btc-tools/schedule/$gamePk/pipelines/$pipelineId',
  });
  const navigate = useNavigate({ from: '/btc-tools/schedule/$gamePk/pipelines/$pipelineId' });

  const { data, loading, networkStatus, refetch } = useQuery(BTC_GAME_CONTENT_WITH_ASSETS, {
    fetchPolicy: 'cache-and-network',
    variables: { gamePk, scheduleDate: date },
  });

  const mediaIds = pipeline.media.map(({ id }) => id);
  const contentByMediaId = new Map(
    data?.game?.content?.map((content) => [content.mediaId, content]),
  );

  const handleSelectPlayback = (asset: BtcGameAsset, mediaId: string) => {
    navigate({
      replace: true,
      resetScroll: false,
      search: (params) => ({ ...params, asset: generateAssetKey(asset), mediaId }),
    });
  };

  return (
    <section className="relative min-h-56">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-bold">Media</h2>
        <div className="flex gap-2">
          <BulkAssetUpdatesDialog mediaIds={mediaIds} />

          <UpdatePipelineMediaButton pipeline={pipeline} />

          <InfoButton description="Refetches the media to ensure the UI is up to date." size="sm">
            <InfoButtonTrigger disabled={loading} onClick={() => refetch()}>
              <RefreshCcw />
              Refresh Media
            </InfoButtonTrigger>
          </InfoButton>
        </div>
      </div>
      {networkStatus === NetworkStatus.loading ? (
        <PageLoader />
      ) : (
        <div className="animate-fade-in">
          {mediaIds.length ? (
            <div className="space-y-2">
              {mediaIds.map((mediaId) => {
                const content = contentByMediaId.get(mediaId);
                if (!content) return null;

                const { assets, mediaState } = content;

                return (
                  <div key={mediaId}>
                    <Collapsible
                      ref={(ref) => {
                        if (mediaId === mediaIdParam) {
                          ref?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      data-testid={`content-${mediaId}`}
                      defaultOpen={mediaId === mediaIdParam}>
                      <Card className="w-full overflow-x-auto p-2">
                        <MediaDetails
                          actionButton={
                            <CollapsibleTrigger asChild>
                              <Button
                                className="group mr-4"
                                size="icon-lg"
                                title="expand"
                                variant="ghost">
                                <ChevronUp className="transition-transform group-data-[state='closed']:rotate-180" />
                              </Button>
                            </CollapsibleTrigger>
                          }
                          content={content}
                          gamePk={gamePk}
                        />
                        <CollapsibleContent>
                          <div className="mt-4 p-2">
                            <PipelineAssets
                              assets={assets}
                              className="[&_table]:bg-background"
                              gamePk={gamePk}
                              mediaId={mediaId}
                              pipelineStatus={pipeline.status}
                              streamState={pipeline.streamState}
                              onSelectPlayback={handleSelectPlayback}>
                              <h2 className="ml-1 font-bold">Assets</h2>
                            </PipelineAssets>
                          </div>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>

                    <PlaybackVerificationSheet
                      assets={assets}
                      gamePk={gamePk}
                      mediaId={mediaId}
                      mediaType={mediaState?.mediaType ?? MediaType.VIDEO}
                      pipelineStatus={pipeline.status}
                      streamState={pipeline.streamState}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex h-32 items-center justify-center rounded border border-dashed">
              <p>No Media</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
